/**
 * Tab switching for the search panel's default state.
 *
 * Listeners are DELEGATED on document because the empty state is re-rendered by
 * morph() on every reset — anything bound to a specific strip would be dropped
 * the first time the visitor clears a search.
 *
 * No imports: this file is loaded with a plain <script src>, not through the
 * theme importmap, so an `@theme/...` import here would fail to resolve.
 *
 * Panels are toggled with the `hidden` PROPERTY rather than a class, because
 * assets/predictive-search.js excludes containers inside `[hidden]` from
 * arrow-key navigation. A class-only hide would leave inactive tabs' products
 * keyboard navigable.
 */
(() => {
  const TAB = '[data-told-tab]';

  /** @param {Element} root @param {Element} tab */
  const activate = (root, tab) => {
    const target = tab.getAttribute('data-told-tab');

    for (const other of root.querySelectorAll(TAB)) {
      const isActive = other === tab;
      other.setAttribute('aria-selected', String(isActive));
      // Roving tabindex: only the active tab is in the document tab order, so Tab
      // moves past the whole strip rather than through every tab in it.
      other.tabIndex = isActive ? 0 : -1;
    }

    for (const panel of root.querySelectorAll('[data-told-panel]')) {
      panel.hidden = panel.getAttribute('data-told-panel') !== target;
    }
  };

  /* ---------------------------------------------------------------------------
     THE HOIST.

     morph() replaces every child of .predictive-search-form__content on each
     keystroke, and it RECYCLES nodes rather than removing them — a keyless old
     node is morphed in place into whatever the new markup has at that index. So
     the strip cannot simply be put back afterwards: by then that node has become
     foreign markup. It has to be OUT of the morph target before any morph runs.

     So at boot the strip is lifted one level, to .predictive-search-form__content-wrapper,
     and the content box itself is tagged as the Results panel. It is the element
     morph writes results into, so it IS the panel — nothing is ever re-parented
     on the hot path.

     #resetSearch morphs a whole fresh empty state (including a fresh strip) back
     into the content box, so the hoist has to run again every time that happens.
     A childList observer on the content box drives it.
  --------------------------------------------------------------------------- */

  const RESULTS_KEY = 'told-results';
  const ROOT = 'data-told-tabs';

  const contentBox = () => document.querySelector('#search-modal .predictive-search-form__content');

  /** Lift a freshly rendered strip out of the morph target. @param {Element} tabs */
  const hoist = (tabs) => {
    const content = contentBox();
    const wrapper = content?.parentElement;
    if (!content || !wrapper) return null;

    // The content box is the Results panel. Hidden until results actually land.
    content.setAttribute('data-told-panel', RESULTS_KEY);
    if (!content.hasAttribute('data-told-ready')) {
      content.setAttribute('data-told-ready', '');
      content.hidden = true;
    }

    // Rescue the rail BEFORE dropping the old strip. It was moved inside .told-tabs
    // by a previous hoist, so removing that node would take the rail with it — and
    // #resetSearch re-renders the strip on every Escape, Clear and dialog close, so
    // the rail would vanish the first time any of those happened.
    const rail = wrapper.querySelector('.told-search-suggest');
    if (rail) wrapper.appendChild(rail);

    const previous = wrapper.querySelector(':scope > .told-tabs');
    if (previous && previous !== tabs) previous.remove();

    // The root must be the common ancestor of the strip AND the content box, so it
    // moves to the wrapper; leaving it on .told-tabs would make closest() stop
    // there and never find the Results panel.
    tabs.removeAttribute(ROOT);
    wrapper.insertBefore(tabs, content);

    // Bring the Shop rail INSIDE .told-tabs, ahead of the panels. It used to be
    // absolutely positioned against the wrapper; now that the strip is a sibling at
    // the top of that same wrapper the two overlap. Making it a grid cell instead of
    // an overlay fixes that without re-anchoring it inside .told-tabs__panels, which
    // is a scroll container and would let the rail scroll away.
    const panels = tabs.querySelector('.told-tabs__panels');
    if (rail && panels) tabs.insertBefore(rail, panels);
    wrapper.setAttribute(ROOT, '');
    wrapper.removeAttribute('data-told-has-results');
    return wrapper;
  };

  /** True once the box holds real search results rather than an empty state. */
  const hasResults = (content) => !!content.querySelector('.predictive-search-results__inner[data-search-results]');

  const sync = () => {
    const content = contentBox();
    if (!content) return;

    const stray = content.querySelector('.told-tabs');
    if (stray) {
      // A reset morphed a fresh empty state in. Re-hoist and go back to a collection tab.
      const wrapper = hoist(stray);
      if (!wrapper) return;
      const first = wrapper.querySelector('.told-tabs__tab:not(.told-tabs__tab--results)');
      if (first) activate(wrapper, first);
      return;
    }

    const wrapper = content.parentElement;
    if (!wrapper || !wrapper.hasAttribute(ROOT)) return;

    if (hasResults(content)) {
      // Results have ARRIVED — only now is the tab revealed and activated. Keying
      // off the Clear button instead would fire ~1.5s earlier, collapsing the panel
      // for the whole round trip, and would miss a second keystroke entirely.
      wrapper.setAttribute('data-told-has-results', '');
      const resultsTab = wrapper.querySelector('.told-tabs__tab--results');
      if (resultsTab && resultsTab.getAttribute('aria-selected') !== 'true') activate(wrapper, resultsTab);
    }
  };

  const observe = () => {
    const content = contentBox();
    if (!content) return false;
    const tabs = content.querySelector('.told-tabs');
    if (tabs) hoist(tabs);
    new MutationObserver(sync).observe(content, { childList: true, subtree: true });
    sync();
    return true;
  };

  if (!observe()) {
    // The modal is rendered late on some templates; retry until it exists.
    const boot = new MutationObserver(() => {
      if (observe()) boot.disconnect();
    });
    boot.observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener('click', (event) => {
    const tab = event.target instanceof Element ? event.target.closest(TAB) : null;
    const root = tab?.closest('[data-told-tabs]');
    if (!tab || !root) return;

    event.preventDefault();
    activate(root, tab);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'Home' && event.key !== 'End') return;

    const tab = event.target instanceof Element ? event.target.closest(TAB) : null;
    const root = tab?.closest('[data-told-tabs]');
    if (!tab || !root) return;

    // Hidden tabs (Recently viewed with no history) must not be reachable.
    const tabs = [...root.querySelectorAll(TAB)].filter((el) => el.offsetParent !== null);
    if (tabs.length < 2) return;

    const current = tabs.indexOf(tab);
    if (current === -1) return;

    let next = current;
    if (event.key === 'ArrowLeft') next = current === 0 ? tabs.length - 1 : current - 1;
    if (event.key === 'ArrowRight') next = current === tabs.length - 1 ? 0 : current + 1;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;

    // predictive-search.js returns early on ArrowLeft/ArrowRight, so this cannot
    // fight the input's own combobox navigation.
    event.preventDefault();
    activate(root, tabs[next]);
    /** @type {HTMLElement} */ (tabs[next]).focus();
  });
})();
